/* eslint-disable react/jsx-props-no-spreading */
import { UseFieldConfig } from 'react-final-form'
import { usePatchedField } from 'stores/utils/hooks/usePatchedField'
import { FilledInputProps, TextFieldProps } from '@material-ui/core'
import { convertToInteger } from 'utils/number'
import { useAmount } from '@finanzcheck/catalyst-renderless-components'
import { useStores } from 'stores/utils/hooks/useStores'
import { observer } from 'mobx-react'
import {
  useCallback,
  useMemo,
  useState,
  ClipboardEvent,
  memo,
  useEffect
} from 'react'
import { isTenantSmava } from 'utils/url'
import FieldDataCollector from '../FieldDataCollector'
import { PathFormFieldProps } from '../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../utils/tracking/amplitude/WithFieldInteraction'
import WithIdleFieldChange from '../WithIdleFieldChange'
import { useMeta } from '../../stores/utils/hooks/useMeta'
import Text, { Unit } from '../Input/Text'

interface OwnProps {
  unit?: Unit
  clue?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldConfig?: UseFieldConfig<any>
}

type Props = Exclude<PathFormFieldProps<number>, 'onChangeConstraints'> &
  OwnProps &
  Pick<TextFieldProps, 'InputLabelProps'>

const AmountInput = observer(
  ({
    path,
    unit,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelPath,
    clue,
    onChangeConstraints,
    onTooltip,
    onChange,
    onBlur,
    onFocus,
    fieldConfig,
    ...restProps
  }: Props) => {
    const { input, meta, error } = usePatchedField(
      path,
      {
        ...fieldConfig,
        parse: (v: number) => {
          const stringValue = `${v}`
          if (stringValue.length === 0) {
            return undefined
          }

          return +stringValue.replace(/\D/g, '')
        }
      },
      path !== 'common.vehicle.initialPayment'
    )

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { errorMap, ...restMeta } = useMeta(path)

    const {
      traversal: { removeErrorsForPath, data, handlePathFormFieldChange }
    } = useStores()

    const isSmava = isTenantSmava()

    const [pasteError, setPasteError] = useState<string | undefined>(undefined)

    const focusHandler = useCallback(
      (value: string) => {
        input.onFocus()
        if (onFocus) {
          onFocus(+value)
        }
      },
      [input, onFocus]
    )
    const blurHandler = useCallback(
      (value: string) => {
        input.onBlur()
        if (onBlur) {
          onBlur(+value)
        }
      },
      [input, onBlur]
    )
    const changeHandler = useCallback(
      (value: string) => {
        if (isSmava && data.common.purpose === 'PRE_OWNED_CAR') {
          if (path === 'common.vehicle.initialPayment') {
            handlePathFormFieldChange(
              `common.amount`,
              data.common.vehicle.price - (convertToInteger(value).value || 0)
            )
          }
          if (path === 'common.vehicle.price') {
            handlePathFormFieldChange(
              `common.amount`,
              (convertToInteger(value).value || 0) -
                (data.common.vehicle.initialPayment || 0)
            )
          }
        }

        input.onChange(value)
        if (onChange) {
          onChange(+value)
        }

        setPasteError(undefined)
        removeErrorsForPath(path)
      },
      [
        isSmava,
        data.common,
        path,
        input,
        onChange,
        removeErrorsForPath,
        handlePathFormFieldChange
      ]
    )

    const inputProps: FilledInputProps['inputProps'] = useMemo(
      () => ({
        inputMode: 'numeric',
        type: 'tel',
        maxLength: 11
      }),
      []
    )

    const pasteHandler = useCallback(
      (event: ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()

        const original = event.clipboardData.getData('Text')

        const { complete, value = 0 } = convertToInteger(original)

        if (!complete) {
          setPasteError(
            `Vorsicht: Ihre Eingabe "${original}" enthielt ungültige Zeichen. Es sind nur ganze Zahlen und keine Sonderzeichen erlaubt.`
          )
        }

        input.onChange(value)

        // eslint-disable-next-line no-param-reassign,@typescript-eslint/no-explicit-any
        ;(event.target as any).selectionStart = inputProps.maxLength // the selection should always be at the end after pasting
      },
      [input, setPasteError, inputProps.maxLength]
    )

    const { value, ...restInputProps } = input

    const { getProps, ref } = useAmount({ value })

    useEffect(() => {
      // traversal model in imod's backend services does not offer the option
      // to store the initial price so we need to calculate it on first render
      if (path === 'common.vehicle.initialPayment') {
        const initialPayment = data.common.vehicle.price - data.common.amount
        handlePathFormFieldChange(
          'common.vehicle.initialPayment',
          initialPayment,
          false
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref])

    const numberInput = (
      <Text
        ref={ref}
        {...restMeta}
        {...restProps}
        {...restInputProps}
        {...getProps({ name: path })}
        onPaste={pasteHandler}
        onFocus={focusHandler}
        onBlur={blurHandler}
        onChange={changeHandler}
        error={meta.touched ? error || pasteError : ''}
        unit={unit}
        acceptedInput={/[0-9]/}
        clue={clue}
        inputProps={inputProps}
        onTooltip={onTooltip}
        path={path}
      />
    )

    if (onChangeConstraints) {
      return (
        <WithIdleFieldChange delayInMs={onChangeConstraints.delayInMs}>
          {numberInput}
        </WithIdleFieldChange>
      )
    }

    return numberInput
  }
)

const PathAmountInput = (props: Props) => {
  const { path, labelPath } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <WithFieldInteraction path={path}>
        <AmountInput {...props} />
      </WithFieldInteraction>
    </FieldDataCollector>
  )
}

export default memo(PathAmountInput)
